import { useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { Program } from '../../../../Models/Program';
import { SnackBar } from '../../../../Models/SnackBar';
import { PushNotification } from '../../../../Models/PushNotification';
import { TEMPLATE_ID } from '../../../../Shared/Constants';
import { SendNotification } from '../../../../Services/NotificationService';
import { ApiError } from '../../../../Models/ApiError';
import ProgramsSelection from './Children/ProgramsSelection/ProgramsSelection';
import './SendPushNotificationForm.css';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';

export default function SendPushNotificationForm(props: { programs: Program[]; }) {
    const [programs] = useState<Program[]>(props.programs);
    const [inputs, setInputs] = useState({ message: null, scheduledDateTime: null, selectedPrograms: [] as Program[] });
    const [showSubmitSpinner, setShowSubmitSpinner] = useState(false);
    const [snackBar, setSnackBar] = useState<SnackBar | null>(null);
    const { instance, accounts } = useMsal();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputs(values => ({ ...values, [event.target.name]: event.target.value }))
    }

    const handleChangeSelectedPrograms = (selectedPrograms: Program[]) => {
        setInputs(values => ({ ...values, selectedPrograms: selectedPrograms }))
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setShowSubmitSpinner(true);
        const notification = new PushNotification(
            TEMPLATE_ID,
            { "message": inputs.message },
            inputs.scheduledDateTime ? new Date(inputs.scheduledDateTime).toISOString().slice(0, -1) + '0000Z' : null,
            programs.length === inputs.selectedPrograms.length ? [] : inputs.selectedPrograms.map((p: Program) => p.id) //Sending empty programs list if all programs are selected
        );

        const tokenRequest = {
            scopes: [""],
            account: accounts[0],
        };

        instance
            .acquireTokenSilent(tokenRequest)
            .then((response) => {
                SendNotification(notification, response.idToken)
                    .then(() => {
                        showSnackBar({ severity: "success", message: "Your notification was accepted." });
                        setShowSubmitSpinner(false);
                    })
                    .catch(error => {
                        showSnackBar({ severity: "error", message: error.response.data.errors.map((e: ApiError) => e.description + ' ') });
                        setShowSubmitSpinner(false);
                    });
            })
            .catch((error) => {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(tokenRequest);
                }
            });
    }

    const showSnackBar = (snackBar: SnackBar) => {
        setSnackBar(snackBar);
        setTimeout(() => {
            setSnackBar(null);
        }, 3000);
    }

    const handleCloseSnackBar = () => {
        setSnackBar(null);
    }

    return (
        <>
            {snackBar &&
                <Alert
                    onClose={handleCloseSnackBar}
                    variant="filled"
                    elevation={6}
                    severity={snackBar.severity}
                    className="snackbar"
                >
                    {snackBar.message}
                </Alert>
            }

            <div className="container">
                <form onSubmit={handleSubmit}>
                    <div className="form-group w-100">
                        <TextField
                            className="w-100"
                            name="message"
                            label="Notification message"
                            multiline
                            rows={10}
                            variant="filled"
                            onChange={handleChange}
                            required
                            error={inputs.message != null && !inputs.message}
                        />
                    </div>
                    <div className="form-group w-100">
                        <ProgramsSelection programs={programs} setSelectedPrograms={handleChangeSelectedPrograms} />
                    </div>
                    <div className="form-group w-100">
                        <TextField
                            className="w-100"
                            name="scheduledDateTime"
                            variant="filled"
                            label="Scheduled Date"
                            type="datetime-local"
                            InputProps={{ inputProps: { min: new Date().toISOString().slice(0, -8) } }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-group">
                        <Button
                            type="submit"
                            variant="contained"
                            endIcon={<SendIcon />}
                            disabled={!inputs.message || inputs.selectedPrograms.length === 0 || showSubmitSpinner === true}>
                            {showSubmitSpinner ?
                                <CircularProgress size={20} />
                                :
                                <span>Send</span>
                            }
                        </Button>
                    </div>
                </form>
            </div>
        </>
    )
};